import React from 'react';
import { Card, Text } from '@chatsdet/ui';
import type { HeadlinePanelData, OverviewTone } from '../../../features/digests/overview';

export const toneClass: Record<OverviewTone, string> = {
  success: 'text-success',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
};

const baseClass = { neutral: 'bg-foreground', primary: 'bg-primary' } as const;

/**
 * One large headline panel (Capabilities, Coverage): the big number, what it
 * was last period, the change in words, and a stacked bar. The bar is drawn
 * from plain divs and described by a single `aria-label`.
 */
export function DigestHeadlinePanel({ panel }: { panel: HeadlinePanelData }) {
  const { bar } = panel;
  return (
    <Card variant="bordered" padding="md" className="gap-y-4">
      <div className="flex items-start justify-between gap-4">
        <Text size="xs" className="uppercase tracking-wide text-muted-foreground">
          {panel.title}
        </Text>
        {panel.delta && (
          <Text size="sm" weight="medium" className={`text-right tabular-nums ${toneClass[panel.delta.tone]}`}>
            {panel.delta.text}
          </Text>
        )}
      </div>

      <div className="flex items-baseline gap-3">
        <span className="text-5xl font-semibold tabular-nums leading-none tracking-tight">
          {panel.value}
          {panel.unit && <span className="text-primary">{panel.unit}</span>}
        </span>
        {panel.was && (
          <Text size="sm" className="text-muted-foreground tabular-nums">
            {panel.was}
          </Text>
        )}
      </div>

      <div>
        <div
          role="img"
          aria-label={bar.ariaLabel}
          className="relative flex h-3 w-full overflow-hidden rounded-full bg-muted"
        >
          {bar.basePct > 0 && (
            <div data-segment="base" className={`h-full ${baseClass[bar.base]}`} style={{ width: `${bar.basePct}%` }} />
          )}
          {bar.gainPct > 0 && (
            <div data-segment="gain" className="h-full bg-success" style={{ width: `${bar.gainPct}%` }} />
          )}
          {bar.tickPct !== null && (
            <div
              data-segment="tick"
              className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-foreground"
              style={{ left: `${bar.tickPct}%` }}
            />
          )}
        </div>
        <div className="mt-2 flex items-center justify-between gap-4">
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {bar.leftLabel}
          </Text>
          {bar.rightLabel && (
            <Text size="xs" className="text-muted-foreground tabular-nums">
              {bar.rightLabel}
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
}
